import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import TaskChat from './TaskChat';
import ProposalBlock from './Proposal';

// The one dialog of a card (GET /api/tasks/:id/dialog). An audio remark is
// discussed where its turn is discussed: a card whose every place falls in
// ONE turn shows that turn's chat here — the card's own earlier messages in
// the same timeline, each with where it was written — and what is typed goes
// to the turn's chat, so nothing is said twice in two chats and one agent
// (the turn's) coordinates the turn. A card on several turns shows one chat
// per place (each turn's own) and keeps its own chat as the common decision.
// Any other card keeps its own chat. Nothing is copied, no agent is woken by
// showing history.

export default function Dialog({ task, usernames, currentUser, busy, onOpenCard, onOpenTurn, onOpenRun, renderAttachment, onSentToCard }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);
  const timerRef = useRef(null);
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    try { const r = await api.dialog(task.id); if (seq !== seqRef.current) return; setD(r); setError(null); } catch (e) { if (seq === seqRef.current) setError(e.message); }
  }, [task.id]);
  useEffect(() => { setD(null); load(); }, [load]);
  useEffect(() => {
    const ids = new Set([task.id, ...((d && d.turns) || []).map((t) => t.chatTaskId).filter(Boolean)]);
    const onEv = (e) => {
      const x = e.detail || {};
      const id = (x.message && x.message.task_id) || (x.task && x.task.id) || (x.run && x.run.task_id) || null;
      if (id && ids.has(id)) { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => { timerRef.current = null; load(); }, 150); }
    };
    window.addEventListener('qv:tasks', onEv);
    window.addEventListener('qv:resync', load);
    window.addEventListener('qv:poll', load);   // the stream brings nothing: read on App's timer
    return () => { window.removeEventListener('qv:tasks', onEv); window.removeEventListener('qv:resync', load); window.removeEventListener('qv:poll', load); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [load, d, task.id]);

  if (error && !d) return <div className="tk-error">{error}</div>;
  if (!d) return <div className="tk-muted">Loading the discussion…</div>;
  const attach = (chatTaskId) => (m) => {
    if (m.meta && m.meta.kind === 'card_proposal') return <ProposalBlock taskId={m.task_id || chatTaskId} message={m} onOpenCard={onOpenCard} onChanged={load} />;
    return renderAttachment ? renderAttachment(m) : null;
  };
  const send = (chatTaskId, toCard) => async (body) => { const r = await api.message(chatTaskId, body); load(); if (toCard && onSentToCard) onSentToCard(); return r.message; };
  return (
    <div className="tk-dialog">
      <div className="tk-muted">This card stores the remark and shared decisions. Work with the agent in the relevant turn:</div>
      {(d.turns || []).map((t) => <div key={`${t.locationUuid}.${t.field}.${t.turnIndex}`}>
        <button type="button" className="tk-linkbtn" onClick={() => onOpenTurn && onOpenTurn({ locationUuid: t.locationUuid, field: t.field, i: t.turnIndex })}>Point {t.pointNo} · {t.field} · {t.label} ↗</button>
      </div>)}
      <TaskChat key={task.id} taskId={task.id} messages={d.card.messages} usernames={usernames} currentUser={currentUser} busy={busy} agent={false} onOpenRun={onOpenRun} renderAttachment={attach(task.id)} onSend={send(task.id, true)} />
    </div>
  );
}
